import { router } from '@inertiajs/react'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Plus, Columns3 } from 'lucide-react'
import { useState } from 'react'
import { useSync } from '@/hooks/useSync'
import { Input } from '@/components/ui/input'

interface BoardSummary {
  id: string
  name: string
  slug: string
  description: string | null
  columns_count: number
  created_at: string
}

interface Props {
  boards: BoardSummary[]
}

export default function BoardsIndex({ boards }: Props) {
  useSync('boards')
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    router.post('/boards', { board: { name, description } }, {
      onSuccess: () => { setShowCreate(false); setName(''); setDescription('') }
    })
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Boards</h1>
          <Button onClick={() => setShowCreate(!showCreate)}>
            <Plus className="mr-1 h-4 w-4" /> New Board
          </Button>
        </div>

        {showCreate && (
          <Card className="bg-white border-gray-200">
            <CardContent className="pt-6">
              <form onSubmit={handleCreate} className="space-y-3">
                <Input
                  placeholder="Board name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  autoFocus
                />
                <Input
                  placeholder="Description (optional)"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button type="submit" size="sm">Create</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {boards.length === 0 ? (
          <p className="text-sm text-gray-500">No boards yet. Create your first board to get started.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {boards.map(board => (
              <Card
                key={board.id}
                className="bg-white border-gray-200 cursor-pointer hover:border-gray-300 transition-colors"
                onClick={() => router.visit(`/boards/${board.slug}`)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Columns3 className="h-4 w-4 text-gray-400" />
                    {board.name}
                  </CardTitle>
                  {board.description && (
                    <CardDescription>{board.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-400">
                    {board.columns_count} columns &middot; Created {new Date(board.created_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}
